// src/utils/useInViewAnimation.ts
import { useEffect, useRef } from "react";

export type InViewOptions = IntersectionObserverInit & {
  /** If true, unobserve after first intersection (default true) */
  once?: boolean;
};

export const useInViewAnimation = (
  className = "in-view",
  options: InViewOptions = { threshold: 0.4, rootMargin: "0px 0px 0px 0px", once: true }
) => {
  const elementRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    const el = elementRef.current;
    if (!el || typeof IntersectionObserver === "undefined") return;

    const { once = true, ...observerOptions } = options;

    const observer = new IntersectionObserver((entries, obs) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          el.classList.add(className);
          if (once) obs.unobserve(entry.target);
        }
      });
    }, observerOptions);

    observer.observe(el);

    return () => observer.disconnect();
  }, [className, options]);

  return elementRef;
};
